import React, { useState } from 'react';
import AppHeader from '../../components/ui/AppHeader';
import ActionToolbar from '../../components/ui/ActionToolbar';
import LoanInputForm from './components/LoanInputForm';
import AmortizationTable from './components/AmortizationTable';

const LoanCalculatorDashboard = () => {
  const [calculationData, setCalculationData] = useState(null);
  const [scheduleData, setScheduleData] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);

  // Financial calculation functions
  const calculateMonthlyPayment = (principal, rate, payments) => {
    const monthlyRate = rate / 12 / 100;
    if (monthlyRate === 0) return principal / payments;
    
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, payments)) / 
           (Math.pow(1 + monthlyRate, payments) - 1);
  };

  const calculateLoanAmount = (payment, rate, payments) => {
    const monthlyRate = rate / 12 / 100;
    if (monthlyRate === 0) return payment * payments;
    
    return payment * (Math.pow(1 + monthlyRate, payments) - 1) / 
           (monthlyRate * Math.pow(1 + monthlyRate, payments));
  };

  const calculateNumberOfPayments = (principal, payment, rate) => {
    const monthlyRate = rate / 12 / 100;
    if (monthlyRate === 0) return principal / payment;
    
    // Check if payment is sufficient to cover interest
    const minPayment = principal * monthlyRate;
    if (payment <= minPayment) {
      throw new Error('Payment amount is too small to cover interest');
    }
    
    // Correct formula: n = -log(1 - (PV * r) / PMT) / log(1 + r)
    const numerator = -Math.log(1 - (principal * monthlyRate) / payment);
    const denominator = Math.log(1 + monthlyRate);
    
    return numerator / denominator;
  };

  const calculateInterestRate = (principal, payment, payments) => {
    // Using Newton-Raphson method for interest rate calculation with improved precision
    let rate = 0.05; // Initial guess of 5%
    let tolerance = 0.0000001; // Increased precision
    let maxIterations = 1000; // Increased iterations for better convergence
    
    for (let i = 0; i < maxIterations; i++) {
      const monthlyRate = rate / 12;
      
      if (monthlyRate === 0) {
        // Handle zero interest rate case
        const calculatedRate = 0;
        return calculatedRate * 100;
      }
      
      const factor = Math.pow(1 + monthlyRate, payments);
      const currentPayment = principal * (monthlyRate * factor) / (factor - 1);
      
      const error = currentPayment - payment;
      
      if (Math.abs(error) < tolerance) {
        return rate * 100;
      }
      
      // Improved derivative calculation for Newton-Raphson
      const h = 0.0001; // Small increment for numerical derivative
      const rateUp = rate + h;
      const monthlyRateUp = rateUp / 12;
      const factorUp = Math.pow(1 + monthlyRateUp, payments);
      const paymentUp = principal * (monthlyRateUp * factorUp) / (factorUp - 1);
      
      const derivative = (paymentUp - currentPayment) / h;
      
      if (Math.abs(derivative) < 1e-10) {
        // Avoid division by very small numbers
        rate = rate + (error > 0 ? -0.001 : 0.001);
      } else {
        rate = rate - error / derivative;
      }
      
      // Constrain rate to reasonable bounds
      if (rate < 0) rate = 0.0001;
      if (rate > 1) rate = 0.999;
    }
    
    return rate * 100;
  };

  const generateAmortizationSchedule = (loanAmount, monthlyPayment, annualRate, startDate, paymentFrequency) => {
    const schedule = [];
    let remainingBalance = loanAmount;
    let paymentNumber = 1;
    
    // Fix date parsing to ensure correct first payment date
    let currentDate;
    if (typeof startDate === 'string') {
      // Parse date string carefully to avoid timezone offset issues
      if (startDate?.includes('T')) {
        // ISO string format
        currentDate = new Date(startDate.split('T')[0] + 'T12:00:00.000Z');
      } else if (startDate?.includes('-')) {
        // YYYY-MM-DD format - create date in local timezone at noon to avoid DST issues
        const dateParts = startDate?.split('-');
        currentDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]), 12, 0, 0);
      } else {
        currentDate = new Date(startDate + 'T12:00:00.000Z');
      }
    } else {
      currentDate = new Date(startDate);
      // Ensure we're working in local timezone at noon
      currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), 12, 0, 0);
    }
    
    // Calculate the correct periodic rate based on payment frequency
    const paymentsPerYear = paymentFrequency === 'monthly' ? 12 : 
                           paymentFrequency === 'bi-weekly' ? 26 :
                           paymentFrequency === 'semi-monthly' ? 24 :
                           paymentFrequency === 'quarterly' ? 4 : 1;
    const periodicRate = annualRate / paymentsPerYear / 100;
    
    while (remainingBalance > 0.01 && paymentNumber <= 1000) {
      // Calculate interest payment using the correct periodic rate with higher precision
      const interestPayment = remainingBalance * periodicRate;
      let principalPayment = monthlyPayment - interestPayment;
      
      // Handle final payment
      if (principalPayment > remainingBalance) {
        principalPayment = remainingBalance;
        monthlyPayment = interestPayment + principalPayment;
      }
      
      remainingBalance -= principalPayment;
      
      schedule?.push({
        paymentNumber,
        paymentDate: new Date(currentDate.getTime()), // Create new date instance to avoid reference issues
        paymentAmount: monthlyPayment,
        interestPayment: Math.max(0, interestPayment),
        principalPayment: Math.max(0, principalPayment),
        remainingBalance: Math.max(0, remainingBalance)
      });
      
      // Move to next payment date based on frequency
      if (paymentFrequency === 'monthly') {
        currentDate?.setMonth(currentDate?.getMonth() + 1);
      } else if (paymentFrequency === 'bi-weekly') {
        currentDate?.setDate(currentDate?.getDate() + 14);
      } else if (paymentFrequency === 'semi-monthly') {
        // Semi-monthly: 1st and 15th of each month (or next business day)
        if (currentDate?.getDate() === 1) {
          currentDate?.setDate(15);
        } else {
          currentDate?.setMonth(currentDate?.getMonth() + 1);
          currentDate?.setDate(1);
        }
      } else if (paymentFrequency === 'quarterly') {
        currentDate?.setMonth(currentDate?.getMonth() + 3);
      } else { // annually
        currentDate?.setFullYear(currentDate?.getFullYear() + 1);
      }
      
      paymentNumber++;
    }
    
    return schedule;
  };

  const handleCalculate = async (formData) => {
    setIsCalculating(true);
    
    try {
      // Simulate calculation delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      let {
        purchasePrice,
        downPayment,
        loanAmount,
        numberOfPayments,
        annualInterestRate,
        paymentAmount,
        loanClosingDate,
        firstPaymentDate,
        paymentFrequency,
        points,
        loanDescription
      } = formData;

      // Convert string values to numbers
      purchasePrice = purchasePrice ? parseFloat(purchasePrice) : null;
      downPayment = downPayment ? parseFloat(downPayment) : 0;
      loanAmount = loanAmount ? parseFloat(loanAmount) : null;
      numberOfPayments = numberOfPayments ? parseInt(numberOfPayments) : null;
      annualInterestRate = annualInterestRate ? parseFloat(annualInterestRate) : null;
      paymentAmount = paymentAmount ? parseFloat(paymentAmount) : null;
      points = points ? parseFloat(points) : 0;

      // If purchase price is provided, calculate loan amount from purchase price - down payment
      if (purchasePrice && !loanAmount) {
        loanAmount = Math.max(0, purchasePrice - downPayment);
      }

      // Variable solving logic - calculate missing value
      if (!loanAmount && numberOfPayments && annualInterestRate && paymentAmount) {
        loanAmount = calculateLoanAmount(paymentAmount, annualInterestRate, numberOfPayments);
      } else if (loanAmount && !numberOfPayments && annualInterestRate && paymentAmount) {
        numberOfPayments = Math.ceil(calculateNumberOfPayments(loanAmount, paymentAmount, annualInterestRate));
      } else if (loanAmount && numberOfPayments && !annualInterestRate && paymentAmount) {
        annualInterestRate = calculateInterestRate(loanAmount, paymentAmount, numberOfPayments);
      } else if (loanAmount && numberOfPayments && annualInterestRate && !paymentAmount) {
        paymentAmount = calculateMonthlyPayment(loanAmount, annualInterestRate, numberOfPayments);
      }

      // Calculate loan metrics
      const totalInterest = (paymentAmount * numberOfPayments) - loanAmount;
      const totalCost = loanAmount + totalInterest;
      const totalPayment = paymentAmount * numberOfPayments;
      const pointsCost = loanAmount * (points / 100);
      
      // Calculate first and final payment dates
      // Fix date parsing to ensure correct first payment date
      let startDate;
      if (firstPaymentDate) {
        if (typeof firstPaymentDate === 'string') {
          // Parse date string carefully to avoid timezone offset issues
          if (firstPaymentDate?.includes('T')) {
            // ISO string format
            startDate = new Date(firstPaymentDate.split('T')[0] + 'T12:00:00.000Z');
          } else if (firstPaymentDate?.includes('-')) {
            // YYYY-MM-DD format - create date in local timezone at noon
            const dateParts = firstPaymentDate?.split('-');
            startDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]), 12, 0, 0);
          } else {
            startDate = new Date(firstPaymentDate + 'T12:00:00.000Z');
          }
        } else {
          startDate = new Date(firstPaymentDate);
          // Ensure we're working in local timezone at noon
          startDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 12, 0, 0);
        }
      } else {
        startDate = new Date();
        // Set to noon to avoid DST issues
        startDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 12, 0, 0);
      }
      
      const payoffDate = new Date(startDate);
      const paymentsPerYear = paymentFrequency === 'monthly' ? 12 : 
                             paymentFrequency === 'bi-weekly' ? 26 :
                             paymentFrequency === 'semi-monthly' ? 24 :
                             paymentFrequency === 'quarterly' ? 4 : 1;
      
      // Calculate months to add based on payment frequency
      let monthsToAdd;
      if (paymentFrequency === 'bi-weekly') {
        // For bi-weekly, calculate based on weeks
        const weeksToAdd = (numberOfPayments - 1) * 2;
        const daysToAdd = weeksToAdd * 7;
        payoffDate?.setDate(payoffDate?.getDate() + daysToAdd);
      } else if (paymentFrequency === 'semi-monthly') {
        // For semi-monthly, calculate based on semi-monthly periods
        const semiMonthlyPeriods = numberOfPayments - 1;
        monthsToAdd = Math.floor(semiMonthlyPeriods / 2);
        const remainingPeriods = semiMonthlyPeriods % 2;
        payoffDate?.setMonth(payoffDate?.getMonth() + monthsToAdd);
        if (remainingPeriods > 0) {
          // Add the remaining semi-monthly period
          if (payoffDate?.getDate() === 1) {
            payoffDate?.setDate(15);
          } else {
            payoffDate?.setMonth(payoffDate?.getMonth() + 1);
            payoffDate?.setDate(1);
          }
        }
      } else {
        // For monthly, quarterly, and annually
        const paymentInterval = 12 / paymentsPerYear;
        monthsToAdd = (numberOfPayments - 1) * paymentInterval;
        payoffDate?.setMonth(payoffDate?.getMonth() + monthsToAdd);
      }

      const calculationResult = {
        // Keep original properties for backward compatibility
        purchasePrice,
        downPayment,
        loanAmount,
        monthlyPayment: paymentAmount,
        totalInterest,
        totalCost,
        totalPayments: numberOfPayments,
        payoffDate,
        annualInterestRate,
        pointsCost,
        loanDescription,
        // Add new properties for LoanSummary component
        principal: loanAmount,
        interestRate: annualInterestRate,
        totalPayment: totalPayment,
        firstPaymentDate: firstPaymentDate || startDate?.toISOString()?.split('T')?.[0],
        finalPaymentDate: payoffDate?.toISOString()?.split('T')?.[0],
        paymentFrequency: paymentFrequency || 'monthly'
      };

      // Generate amortization schedule
      const schedule = generateAmortizationSchedule(
        loanAmount,
        paymentAmount,
        annualInterestRate,
        startDate,
        paymentFrequency
      );

      setCalculationData(calculationResult);
      setScheduleData(schedule);
      
      // Save to localStorage for print preview
      localStorage.setItem('currentLoanCalculation', JSON.stringify(calculationResult));
      
      // Also save the form data for state preservation
      localStorage.setItem('currentLoanFormData', JSON.stringify(formData));
      
    } catch (error) {
      console.error('Calculation error:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleClear = () => {
    setCalculationData(null);
    setScheduleData([]);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = async (format) => {
    // Export functionality would be implemented here
    console.log(`Exporting as ${format}`);
    return new Promise(resolve => setTimeout(resolve, 1000));
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Loan Amortization Schedule',
        text: 'Check out this loan calculation',
        url: window.location?.href
      });
    } else {
      navigator.clipboard?.writeText(window.location?.href);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="pt-20 pb-12">
        <div className="container-professional">
          {/* Professional Page Header */}
          <div className="mb-8">
            <div className="text-center max-w-2xl mx-auto space-y-4">
              <h1 className="text-4xl font-bold text-primary bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Loan Amortization Calculator
              </h1>
              <p className="text-lg text-text-secondary leading-relaxed">
                Calculate loan payments, interest, and generate detailed amortization schedules with professional precision.
              </p>
            </div>
          </div>

          {/* Loan Input Form - Now at the top for all screen sizes */}
          <div className="mb-8">
            <LoanInputForm
              onCalculate={handleCalculate}
              onClear={handleClear}
              isCalculating={isCalculating}
            />
          </div>

          {/* Action Toolbar */}
          <div className="mb-8">
            <ActionToolbar
              hasCalculationData={!!calculationData}
              onPrint={handlePrint}
              onExport={handleExport}
              onShare={handleShare}
            />
          </div>

          {/* Results Section */}
          <div className="space-y-8">
            {/* Loan Summary Cards */}
            {calculationData && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in">
                {/* Purchase Price Card - Show only if purchase price was entered */}
                {calculationData?.purchasePrice && (
                  <div className="card-professional-elevated">
                    <div className="card-professional-content">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-text-secondary">Purchase Price</p>
                          <p className="text-2xl font-bold text-accent data-display-currency">
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            })?.format(calculationData?.purchasePrice)}
                          </p>
                        </div>
                        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                          <span className="text-accent font-semibold">🏷️</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Down Payment Card - Show only if down payment was entered */}
                {calculationData?.downPayment > 0 && (
                  <div className="card-professional-elevated">
                    <div className="card-professional-content">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-text-secondary">Down Payment</p>
                          <p className="text-2xl font-bold text-success data-display-currency">
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            })?.format(calculationData?.downPayment)}
                          </p>
                        </div>
                        <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                          <span className="text-success font-semibold">💰</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="card-professional-elevated">
                  <div className="card-professional-content">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-text-secondary">Loan Amount</p>
                        <p className="text-2xl font-bold text-primary data-display-currency">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          })?.format(calculationData?.loanAmount)}
                        </p>
                      </div>
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <span className="text-primary font-semibold">$</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-professional-elevated">
                  <div className="card-professional-content">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-text-secondary">Monthly Payment</p>
                        <p className="text-2xl font-bold text-warning data-display-currency">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })?.format(calculationData?.monthlyPayment)}
                        </p>
                      </div>
                      <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                        <span className="text-warning font-semibold">↗</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Amortization Table */}
            <div className="animate-slide-up">
              <AmortizationTable scheduleData={scheduleData} />
            </div>
          </div>
        </div>
      </main>
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          body {
            background: white !important;
          }
          
          .print-break {
            page-break-before: always;
          }
          
          table {
            page-break-inside: avoid;
          }
          
          tr {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export default LoanCalculatorDashboard;